/**
 * StatusHistoryDrawer — calm vertical timeline of a child's status updates.
 *
 * Pulls from `/api/children/{id}/status/history` and renders rows newest-first.
 * Used on the Parent /home ("How today unfolded") and the Staff dashboard
 * ("Recent status updates" per-child popover).
 *
 * Iteration 21 — the real-time child status surface.
 */
import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Sparkles } from "lucide-react";
import { api } from "../lib/api";

const STATUS_LABEL = {
  yard: "In the yard",
  play: "At play",
  eat: "Sharing a meal",
  rest: "Resting",
  in_care: "In care",
  arrived: "Just arrived",
  checked_out: "Checked out",
  absent: "Resting at home",
  sick: "Resting (unwell)",
  vacation: "On holiday",
};

const SOURCE_LABEL = {
  parent_app: "via parent",
  staff_dashboard: "via staff",
  child_interface: "via kiosk",
  admin_panel: "via admin",
  scheduler: "by the room",
};

const ROLE_LABEL = {
  child: "child",
  staff: "staff",
  admin: "admin",
};

function fmtTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function ageSeconds(iso) {
  if (!iso) return null;
  return (Date.now() - new Date(iso).getTime()) / 1000;
}

function bucketFor(status, sec) {
  if (["checked_out", "absent", "sick", "vacation"].includes(status))
    return "not_applicable";
  if (sec == null) return "needs_confirmation";
  if (sec < 10 * 60) return "fresh";
  if (sec < 25 * 60) return "aging";
  return "needs_confirmation";
}

const DOT_COLOR = {
  fresh: "#1A1B2E",
  aging: "#D4A85F",
  needs_confirmation: "#A3A3A3",
  not_applicable: "#1F3A2A",
};

export default function StatusHistoryDrawer({
  childId,
  title = "How today unfolded",
  defaultOpen = false,
  testid = "status-history-drawer",
  limit = 20,
  days = 1,
  showSource = true,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !childId) return;
    let alive = true;
    setLoading(true);
    api
      .get(
        `/children/${childId}/status/history?days=${days}&limit=${limit}`
      )
      .then((r) => alive && setRows(r.data || []))
      .catch(() => alive && setRows([]))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [open, childId, days, limit]);

  return (
    <section className="mt-6" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`${testid}-toggle`}
        aria-expanded={open}
        className="w-full flex items-center justify-between rounded-2xl bg-white border hairline px-5 py-4 hover:bg-[#F2EFEA] transition-colors text-left"
      >
        <span className="flex items-center gap-3">
          <span
            aria-hidden
            className="h-9 w-9 rounded-2xl bg-[#F2EFEA] flex items-center justify-center"
          >
            <Sparkles size={14} strokeWidth={1.6} className="text-[#1A1B2E]" />
          </span>
          <span>
            <span className="block font-serif text-base text-[#171717]">
              {title}
            </span>
            <span className="block text-[11px] tracking-[0.18em] uppercase text-[#525252] mt-0.5">
              {open ? "Tap to fold" : "A gentle timeline"}
            </span>
          </span>
        </span>
        {open ? (
          <ChevronUp size={16} strokeWidth={1.6} className="text-[#525252]" />
        ) : (
          <ChevronDown size={16} strokeWidth={1.6} className="text-[#525252]" />
        )}
      </button>

      {open && (
        <div
          data-testid={`${testid}-body`}
          className="mt-3 rounded-2xl bg-white border hairline overflow-hidden"
        >
          {loading ? (
            <div className="px-5 py-6 font-serif italic text-[#525252] text-sm">
              A quiet moment…
            </div>
          ) : rows.length === 0 ? (
            <div
              data-testid={`${testid}-empty`}
              className="px-5 py-6 font-serif italic text-[#525252] text-sm"
            >
              Nothing to gently recount yet today.
            </div>
          ) : (
            <ol className="divide-y divide-[#171717]/6">
              {rows.map((r) => {
                const bucket = bucketFor(r.status, ageSeconds(r.at));
                return (
                  <li
                    key={r.id}
                    data-testid={`${testid}-row-${r.id}`}
                    className="px-5 py-4 flex gap-4 items-start"
                  >
                    <span
                      aria-hidden
                      className="h-2.5 w-2.5 rounded-full mt-1.5 shrink-0"
                      style={{ background: DOT_COLOR[bucket] }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-serif text-[15px] text-[#171717]">
                        {STATUS_LABEL[r.status] || r.status}
                      </div>
                      <div className="text-xs text-[#525252] mt-0.5">
                        {r.updated_by_user_name || "—"}
                        {showSource && (
                          <span className="text-[#9CA3AF]">
                            {" "}
                            · {ROLE_LABEL[r.updated_by_role] || r.updated_by_role}
                            {" "}
                            {SOURCE_LABEL[r.source] || ""}
                          </span>
                        )}
                      </div>
                      {r.note && (
                        <div className="text-xs text-[#525252] mt-1 italic">
                          “{r.note}”
                        </div>
                      )}
                    </div>
                    <span className="text-[10px] tracking-[0.18em] uppercase text-[#525252] shrink-0 mt-1">
                      {fmtTime(r.at)}
                    </span>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      )}
    </section>
  );
}
